import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import expensesService from '../../services/expensesService';
import { settingsService } from '../../services/settingsService';

const ExpenseEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [cnyRate, setCnyRate] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    expense_date: '',
    original_amount: '',
    currency: 'NGN',
    vendor_name: '',
    payment_method: '',
    receipt: null
  });

  const [preview, setPreview] = useState({
    amount_in_ngn: 0,
    amount_in_cny: 0,
    exchange_rate: 0
  });

  // Removed fetchRate as we use the stored rate from the expense data

  const fetchExpense = useCallback(async () => {
    try {
      setLoading(true);
      const data = await expensesService.getExpense(id);
      
      // Check if editable
      if (data.status !== 'draft' || data.is_locked) {
        setError('This expense cannot be edited because it is approved, rejected, or locked.');
        setLoading(false);
        return;
      }

      setFormData({
        title: data.title,
        description: data.description || '',
        category: data.category,
        expense_date: data.expense_date || '',
        original_amount: data.original_amount,
        currency: data.currency,
        vendor_name: data.vendor_name || '',
        payment_method: data.payment_method || '',
        receipt: null // We don't pre-fill file input, but could show existing receipt link
      });

      // Use the exchange rate stored in the expense
      if (data.exchange_rate_used) {
        setCnyRate(parseFloat(data.exchange_rate_used));
      } else if (data.exchange_rate) {
        setCnyRate(parseFloat(data.exchange_rate));
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to edit this expense.');
      } else {
        setError('Failed to fetch expense details');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  const calculatePreview = useCallback(() => {
    const amount = parseFloat(formData.original_amount);
    if (!amount || isNaN(amount)) {
      setPreview({ amount_in_ngn: 0, amount_in_cny: 0, exchange_rate: cnyRate || 0 });
      return;
    }

    let ngn = 0;
    let cny = 0;
    const rate = cnyRate || 0;

    if (formData.currency === 'NGN') {
      ngn = amount;
      cny = rate > 0 ? amount / rate : 0;
    } else if (formData.currency === 'CNY') {
      cny = amount;
      ngn = amount * rate;
    }

    setPreview({
      amount_in_ngn: ngn,
      amount_in_cny: cny,
      exchange_rate: rate
    });
  }, [formData.original_amount, formData.currency, cnyRate]);

  useEffect(() => {
    fetchExpense();
  }, [id, fetchExpense]);

  useEffect(() => {
    calculatePreview();
  }, [calculatePreview]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [receiptPreview, setReceiptPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, receipt: file });
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        // Only append receipt if it's a new file
        if (key === 'receipt' && !(formData[key] instanceof File)) {
            return;
        }
        data.append(key, formData[key]);
      }
    });

    try {
      await expensesService.updateExpense(id, data);
      navigate(`/admin/expenses/${id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to edit this expense.');
      } else {
        setError(err.response?.data?.detail || 'Failed to update expense');
      }
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mb: 3, 
      flexWrap: 'wrap', 
      gap: 2 
    }}>
      <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Edit Expense
        </Typography>

        <Card>
          <CardContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Expense Date"
                    name="expense_date"
                    value={formData.expense_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Amount & Currency
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Original Amount"
                    name="original_amount"
                    value={formData.original_amount}
                    onChange={handleChange}
                    required
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    required
                    disabled={!!error}
                  >
                    <MenuItem value="NGN">NGN (Nigerian Naira)</MenuItem>
                    <MenuItem value="CNY">CNY (Chinese Yuan)</MenuItem>
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" icon={false} sx={{ bgcolor: 'action.hover' }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" display="block" color="text.secondary">Exchange Rate</Typography>
                        <Typography variant="body1">{preview.exchange_rate || '-'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" display="block" color="text.secondary">Amount in NGN</Typography>
                        <Typography variant="body1">₦{preview.amount_in_ngn.toLocaleString()}</Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" display="block" color="text.secondary">Amount in CNY</Typography>
                        <Typography variant="body1">¥{preview.amount_in_cny.toLocaleString()}</Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Additional Details
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Vendor Name"
                    name="vendor_name"
                    value={formData.vendor_name}
                    onChange={handleChange}
                    disabled={!!error}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Method"
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    disabled={!!error}
                  >
                    <MenuItem value="">Select Method</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                    <MenuItem value="alipay">Alipay</MenuItem>
                    <MenuItem value="wechat">WeChat Pay</MenuItem>
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Update Receipt (Optional)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button variant="outlined" component="label" disabled={!!error}>
                        {formData.receipt ? 'Change Receipt' : 'Upload Receipt'}
                        <input
                          type="file"
                          hidden
                          accept="image/*,application/pdf"
                          onChange={handleFileChange}
                        />
                      </Button>
                      {formData.receipt && formData.receipt instanceof File && (
                        <Typography variant="body2" color="text.secondary">
                          {formData.receipt.name}
                        </Typography>
                      )}
                    </Box>
                    {receiptPreview && (
                      <Box
                        component="img"
                        src={receiptPreview}
                        alt="Receipt Preview"
                        sx={{
                          width: '100%',
                          maxWidth: 300,
                          maxHeight: 300,
                          objectFit: 'contain',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mt: 1
                        }}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/admin/expenses')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={saving || !!error}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ExpenseEdit;
